import { runInAction } from 'mobx'
import rootStore from '../..' // Import the root store
import { Budget } from '../../../cls/modules/budget'
import { setBudget } from '../../../api/modules/api-budget'
import { cleanObject, warnPt } from '../../../config'
import { responseHasError } from '../../users/actions'
import { wrapWithLoading } from '../../../config/general-actions'
import { actSaveEntityForDemo } from '../../monthly-reports/actions'
import { getFullItm } from '../../../api/utils/api-item'
import { smartT } from '../../../config/translationGlobal'

const budgetStore = rootStore.budget

/**
 * @async
 * @function newBudget
 * @description Creates a new budget item with the specified ID and marks it as new.
 * @param {string} newId - The ID of the new budget item.
 * @returns {Promise<void>}
 */
export const newBudget = async (newId) => {
	warnPt(newId)
	runInAction(() => {
		const _storeItm = requireBudgetById(newId)
		_storeItm.setNewFlag()
	})
}

/**
 * @function requireBudgetById
 * @description Retrieves a budget item by its ID from the store. If it doesn't exist, creates a new budget item.
 * @param {string} budgetId - The ID of the budget item to retrieve.
 * @returns {Budget} The retrieved or newly created budget item.
 */
export const requireBudgetById = (budgetId) => {
	warnPt(budgetId)
	let budget = budgetStore.budget[budgetId]
	if (budget) {
		return budget
	}
	runInAction(() => {
		budgetStore.budget[budgetId] = new Budget(budgetId)
	})
	return budgetStore.budget[budgetId]
}

/**
 * @async
 * @function actLoadBudget
 * @description Loads a budget item from the server by its ID and updates the store.
 * @param { function } navigate - The navigation function.
 * @param {string} budgetId - The ID of the budget item to load.
 * @returns {Promise<void>}
 */
export const actLoadBudget = wrapWithLoading(
	async (budgetId) => {
		const _response = await getFullItm('budgets', {
			id: budgetId
		})

		const hasError = responseHasError(_response)
		if (!hasError && _response.status === 'success') {
			runInAction(() => {
				const _storeItm = requireBudgetById(budgetId)
				_storeItm.updateFromServer(_response.data || {})
			})
		} else {
			throw _response
		}
	},
	{ loadingMessage: smartT('__loading%_budget') }
)

/**
 * @async
 * @function actSaveBudget
 * @description Saves a budget item to the server.
 * @param {string} budgetId - The ID of the budget item to save.
 * @param {Object} full - The full data of the budget item to save.
 * @returns {Promise<boolean>} True if the save was successful, false otherwise.
 */
export const actSaveBudget = wrapWithLoading(
	async (budgetId, full, demo = false) => {
		if (demo) {
			actSaveEntityForDemo(
				budgetId,
				full,
				requireBudgetById,
				rootStore.uiLstBudget,
				false
			)

			return true
		}

		full = cleanObject(full)
		const _response = await setBudget(budgetId, full)

		const { data } = _response
		const hasError = data ? responseHasError(data) : true

		if (hasError) {
			throw data
		} else {
			const _storeItm = requireBudgetById(budgetId)
			_storeItm.setModified(data.modified)
		}
		return true // No exceptions were thrown, so return true
	},
	{ loadingMessage: 'Saving Budget' }
)
